import { useState, useEffect, useMemo } from 'react';
import { Switch, FormControlLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client/react';
import { CANCEL_ORDER, CREDIT_ORDER, GET_ORDERS, RETRY_PURCHASE } from './Orders.graphql';
import { Order } from '../../interfaces';
import { GridValidRowModel, DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid-pro/locales';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';

import {
  Cancel,
  CancelRounded,
  ErrorRounded,
  CheckCircleRounded,
  CreditCard,
  AttachMoney,
  PictureAsPdf,
  Receipt,
  Info,
  Lens,
  LensOutlined,
  NotInterestedOutlined,
  GetApp,
  Undo,
  Replay,
} from '@mui/icons-material';
import { formatDate, numberAsKr } from '../../Formatters';
import { OrderStatus, OrderType, PaymentMode } from '../../interfaces.enums';
import ConfirmDialog from '../../components/ConfirmDialog';
import ConfirmWithInvoiceDialog from './ConfirmWithInvoiceDialog';
import { useConfig } from '../../components/Configuration';
import Loading from '../../components/LoadingSpinner';
import { SearchBox } from './SearchBox'; // Import our new component

interface Props {
  showIncomplete: boolean;
  setShowIncomplete(val: boolean): void;
  licenseId?: number;
}

export function OrderSearchableTable({ showIncomplete, setShowIncomplete, licenseId }: Props) {
  const getRetryPurchaseErrorMessage = (error: Error, orderNumber: number): string => {
    const firstGraphQlError = ((error as any)?.graphQLErrors ?? [])?.[0];
    const errorCode = firstGraphQlError?.extensions?.code;
    console.log({ error, errorCode });
    const rawMessage = firstGraphQlError?.message || error.message;


    if (errorCode === 'UNAUTHORIZED') {
      return `Ordre ${orderNumber} er ikke autorisert og kan ikke kjøres på nytt.`;
    }

    if (errorCode === 'RETRY_FAILED') {
      return `Kjøpsprosessen for ordre ${orderNumber} ble ikke fullført.`;
    }

    return rawMessage;
  };

  const [showCard, setShowCard] = useState(true);
  const [showInvoice, setShowInvoice] = useState(true);
  const [showNonInvoiceable, setShowNonInvoiceable] = useState(true);

  // Search states
  const [backendSearchTerm, setBackendSearchTerm] = useState('');
  const [isBackendSearch, setIsBackendSearch] = useState(false);
  const [clientFilterValue, setClientFilterValue] = useState('');

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 200,
    page: 1,
  });
  const [isDataLoading, setIsDataLoading] = useState(true);
  const { pageSize, page } = paginationModel;
  const config = useConfig();
  const pollIntervall = config ? config.defaultPollingInterval : 0;
  const apiRef = useGridApiRef();

  // In OrderSearchableTable.tsx, add a new state:
  const [clientSearchLoading, setClientSearchLoading] = useState(false);

  // Unified search handler for the SearchBox component
  const handleSearch = (searchValue: string, useBackendSearch: boolean) => {
    if (!useBackendSearch) {
      // For client-side search, show loading indicator
      setClientSearchLoading(true);

      // Clear it after a short delay to ensure it's visible
      setTimeout(() => setClientSearchLoading(false), 400);
    }
    setIsBackendSearch(useBackendSearch);

    if (useBackendSearch) {
      // Backend search
      setBackendSearchTerm(searchValue);
      setClientFilterValue(''); // Clear client filter
      setPaginationModel({
        ...paginationModel,
        page: 1, // Reset to first page for backend search
      });
    } else {
      // Client search
      setClientFilterValue(searchValue);
      setBackendSearchTerm(''); // Clear backend search
    }
  };

  // Clear search handler
  const clearSearch = () => {
    setClientFilterValue('');
    setBackendSearchTerm('');
    setIsBackendSearch(false);
  };

  // Update query to include backend search term when active
  const { loading, data } = useQuery<any, any>(GET_ORDERS, {
    variables: licenseId
      ? {
        showIncomplete,
        page,
        pageSize,
        licenseId,
        searchTerm: isBackendSearch ? backendSearchTerm : undefined,
      }
      : {
        showIncomplete,
        page,
        pageSize,
        searchTerm: isBackendSearch ? backendSearchTerm : undefined,
      },
    fetchPolicy: 'no-cache',
    pollInterval: isBackendSearch ? 0 : pollIntervall, // Disable polling during backend search
  });

  const isLoading = loading || !data;

  const orders: Order[] = [];

  console.log({ data });

  useEffect(() => {
    if (!loading && data) {
      setIsDataLoading(true);
      const renderTimeout = setTimeout(() => setIsDataLoading(false), 200); // Adjust time as needed
      return () => clearTimeout(renderTimeout); // cleanup on component unmount or data change
    }
  }, [loading, data]);

  if (data && data.orders) {
    orders.push(
      ...data.orders.orders.map((order) => ({
        id: order.id,
        orderNumber: order.orderNumber,
        invoiceNumber: order.invoiceNumber,
        creditInvoiceNumber: order.creditInvoiceNumber,
        registrationEmail: order.registrationEmail,
        user: order.user,
        license: order.license,
        amount: order.amount,
        description: order.description,
        type: order.type,
        paymentMode: order.paymentMode,
        status: order.status,
        startedAt: order.startedAt,
        invoiceContentId: order.invoiceContentId,
        creditInvoiceContentId: order.creditInvoiceContentId,
        errorMessage: order.errorMessage,
        isInvoiceable: order.isInvoiceable,
      })),
    );
  }

  const isAuthorized = (order: Order): boolean => {
    return (
      order.status === OrderStatus.Completed ||
      order.status === OrderStatus.Fullfilled ||
      order.status === OrderStatus.Credited ||
      (order.type === OrderType.Invoice && order.status === OrderStatus.Open)
    );
  };

  const filteredOrders = orders.filter(
    (x) =>
      (showCard || x.type !== OrderType.Card) &&
      (showInvoice || x.type !== OrderType.Invoice || (showNonInvoiceable && !x.isInvoiceable)) &&
      (showNonInvoiceable || x.isInvoiceable) &&
      (showIncomplete || isAuthorized(x)),
  );

  useEffect(() => {
    if (data) {
      setRowCountState(data.orders.count);
    }
  }, [data]);

  const rowCount = data?.orders.count ?? 0;
  const [rowCountState, setRowCountState] = useState(rowCount);

  // Base rows from filtered orders
  const baseRows: GridValidRowModel[] = useMemo(
    () =>
      filteredOrders.map((x) => ({
        id: x.id,
        userId: x.user?.id,
        licenseId: x.license?.id,
        orderNumber: x.orderNumber,
        user: x.user?.displayName,
        customer: x.license?.companyName,
        description: x.description,
        amount: x.amount,
        type: x.type,
        status: x.status,
        isInvoiceable: x.isInvoiceable,
        paymentMode: x.paymentMode,
        date: x.startedAt,
        pdf: x.invoiceNumber,
        action: '...',
        errorMessage: x.errorMessage,
        invoiceContentId: x.invoiceContentId,
        creditInvoiceContentId: x.creditInvoiceContentId,
        creditInvoiceNumber: x.creditInvoiceNumber,
        invoiceNumber: x.invoiceNumber,
        registrationEmail: x.registrationEmail,
      })),
    [filteredOrders],
  );

  // Add this function at the top of your file
  // Calculates how similar two strings are (0-100)
  const calculateSimilarity = (str1: string, str2: string): number => {
    // Convert both to lowercase for case-insensitive comparison
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();

    // Direct match gets maximum score
    if (s1 === s2) return 100;

    // For emails, special handling
    if (s1.includes('@') && s2.includes('@')) {
      const [user1, domain1] = s1.split('@');
      const [user2, domain2] = s2.split('@');

      // If either username or domain is an exact match, high score
      if (user1 === user2) return 80;
      if (domain1 === domain2) return 70;

      // Calculate partial match score for username and domain
      let usernameScore = 0;
      let domainScore = 0;

      // Username direct contains
      if (user1.includes(user2) || user2.includes(user1)) {
        usernameScore = 60;
      }

      // Domain direct contains
      if (domain1.includes(domain2) || domain2.includes(domain1)) {
        domainScore = 50;
      }

      return Math.max(usernameScore, domainScore);
    }

    // For normal strings
    if (s1.includes(s2) || s2.includes(s1)) {
      // One contains the other, score based on length ratio
      const lengthRatio = Math.min(s1.length, s2.length) / Math.max(s1.length, s2.length);
      return 50 + Math.round(lengthRatio * 40); // Max 90 for contains
    }

    // Check for word matching (e.g., "trading" in "rufa-trading")
    const words1 = s1.split(/[\s\-_.,;:]/);
    const words2 = s2.split(/[\s\-_.,;:]/);

    for (const word1 of words1) {
      if (word1.length < 3) continue; // Skip short words
      for (const word2 of words2) {
        if (word2.length < 3) continue;
        if (word1 === word2) return 40; // Exact word match
        if (word1.includes(word2) || word2.includes(word1)) return 30; // Partial word match
      }
    }

    // Fall back to checking if either string contains parts of the other
    if (s1.length > 3 && s2.includes(s1.substring(0, Math.floor(s1.length / 2)))) return 20;
    if (s2.length > 3 && s1.includes(s2.substring(0, Math.floor(s2.length / 2)))) return 20;

    return 0; // No similarity found
  };

  // And then replace your displayRows useMemo with:
  const displayRows = useMemo(() => {
    if (!isBackendSearch && clientFilterValue) {
      const searchValue = clientFilterValue.toLowerCase().trim();

      // Create an array of just the searchable fields
      const searchableFields = ['orderNumber', 'user', 'customer', 'registrationEmail'];

      // Calculate similarity scores for each row
      const scoredRows = baseRows.map((row) => {
        let maxScore = 0;
        let matchField = '';

        // Check each field for similarity
        searchableFields.forEach((field) => {
          const value = row[field];
          if (value == null) return;

          const stringValue = String(value).toLowerCase();

          // First check for exact match (traditional method)
          if (stringValue.includes(searchValue)) {
            const directMatchScore = 85; // High score for direct matches
            if (directMatchScore > maxScore) {
              maxScore = directMatchScore;
              matchField = field;
            }
          } else {
            // Otherwise check for fuzzy matches
            const similarity = calculateSimilarity(stringValue, searchValue);
            if (similarity > maxScore) {
              maxScore = similarity;
              matchField = field;
            }
          }
        });

        // Return the row with its similarity score
        return {
          ...row,
          __similarity: maxScore,
          __matchField: matchField,
        };
      });

      // Filter rows with a minimum similarity score (adjust threshold as needed)
      const filteredRows = scoredRows.filter((row) => row.__similarity >= 20);

      // Sort by similarity score (highest first)
      filteredRows.sort((a, b) => b.__similarity - a.__similarity);

      return filteredRows;
    }

    // Return base rows if no filter
    return baseRows;
  }, [baseRows, isBackendSearch, clientFilterValue]);

  // Prepare search info message for the SearchBox component
  const searchInfo =
    isBackendSearch && backendSearchTerm
      ? `Søker database på: "${backendSearchTerm}"`
      : clientFilterValue
        ? `Filtrert på: "${clientFilterValue}"`
        : '';

  const columns: GridColDef[] = [
    // Your columns definition remains the same
    {
      field: 'orderNumber',
      headerName: 'Nummer',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => <span>{params.value}</span>,
    },
    {
      field: 'user',
      headerName: 'Bruker',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const { userId, user, registrationEmail } = params.row || {};

        if (userId) {
          return <Link to={`/user/${userId}`}>{user}</Link>;
        }

        // If no userId but has registrationEmail, show the email
        if (registrationEmail) {
          return <span style={{ fontStyle: 'italic', color: '#666' }}>{registrationEmail}</span>;
        }

        // Only show "Slettet" if there's no user and no email
        return <span>&lt;Slettet&gt;</span>;
      },
    },
    {
      field: 'customer',
      headerName: 'Kunde',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const { licenseId, customer } = params.row || {};
        return licenseId ? <Link to={`/license/${licenseId}`}>{customer || '<Mangler navn>'}</Link> : null;
      },
    },
    {
      field: 'description',
      headerName: 'Beskrivelse',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <span title={params.value} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.value}
        </span>
      ),
    },
    {
      field: 'amount',
      headerName: 'Beløp',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const { isInvoiceable, status } = params.row;
        const amount = numberAsKr(params.value);
        if (!isInvoiceable) return `Ikke-fakturert ${amount}`;
        if (status === OrderStatus.Credited) return `Kreditert ${amount}`;
        return amount;
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const { isInvoiceable, type } = params.row;
        if (!isInvoiceable) return <NotInterestedOutlined titleAccess="Ikke-fakturerbar" />;
        if (type === OrderType.Card) return <CreditCard titleAccess="Kort" />;
        if (type === OrderType.Invoice) return <AttachMoney titleAccess="Faktura" />;
        return type;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const { status, errorMessage, paymentMode, type, isInvoiceable } = params.row;
        switch (status) {
          case OrderStatus.Open:
            return <LensOutlined titleAccess="Åpen" />;
          case OrderStatus.Cancelled:
            return <CancelRounded color="error" titleAccess="Avbrutt" />;
          case OrderStatus.Failed:
            return <ErrorRounded color="error" titleAccess={`Feilet: ${errorMessage}`} />;
          case OrderStatus.Reserved:
            return <Lens color="success" titleAccess="Beløp reservert" />;
          case OrderStatus.Completed:
            if (type === OrderType.Card) return <CheckCircleRounded color="success" titleAccess="Betalt" />;
            if (!isInvoiceable) return <CheckCircleRounded color="success" titleAccess="Markert som ikke-fakturerbar" />;
            if (paymentMode === PaymentMode.Manual) return <Info titleAccess="Håndteres manuelt i Maconomy" />;
            return <CheckCircleRounded color="success" titleAccess="Faktura sendt" />;
          case OrderStatus.Fullfilled:
            return <CheckCircleRounded color="success" titleAccess="Faktura sendt manuelt" />;
          case OrderStatus.Credited:
            return <CheckCircleRounded titleAccess="Kreditert" />;
          default:
            return status;
        }
      },
    },
    {
      field: 'date',
      headerName: 'Dato',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'pdf',
      headerName: 'Faktura',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const { invoiceContentId, invoiceNumber, creditInvoiceContentId, creditInvoiceNumber } = params.row;

        const rootUrl = '/server/api';

        return (
          <>
            {invoiceContentId ? (
              <a href={rootUrl + '/content/' + invoiceContentId} target="_blank" rel="noreferrer">
                <PictureAsPdf titleAccess="Åpne faktura i ny fane" />
              </a>
            ) : invoiceNumber ? (
              <Receipt titleAccess={`Faktura opprettet: ${invoiceNumber}`} />
            ) : null}
            {creditInvoiceContentId ? (
              <a href={rootUrl + '/content/' + creditInvoiceContentId} target="_blank" rel="noreferrer">
                <PictureAsPdf titleAccess="Åpne kreditnota i ny fane" />
              </a>
            ) : creditInvoiceNumber ? (
              <Receipt titleAccess={`Kreditnota opprettet: ${creditInvoiceNumber}`} />
            ) : null}
          </>
        );
      },
    },
    {
      field: 'action',
      headerName: '',
      minWidth: 120,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        return <Action order={params.row} />;
      },
    },
  ];



  const Action = ({ order }: { order: Order }) => {
    if (order.type === OrderType.Card && order.status === OrderStatus.Open) {
      return (
        <div style={{ display: 'flex', gap: '4px' }}>
          <AbortAction order={order} />
          <RetryPurchaseAction order={order} />
        </div>
      );
    }

    if (order.type === OrderType.Card && order.status === OrderStatus.Reserved) {

      return (<AbortAction order={order} />);
    }

    if (order.status === OrderStatus.Failed) {
      return (
        <div style={{ display: 'flex', gap: '4px' }}>

          <UpdateStatusAction order={order} />
          <RetryPurchaseAction order={order} />
        </div>
      );
    }



    if (order.invoiceNumber && !order.creditInvoiceNumber) {
      return <CreditAction order={order} />;
    } else return null;
  };


  function AbortAction({ order }: { order: Order }) {
    const [showConfirm, setShowConfirm] = useState(false);
    const [cancel, { called, loading }] = useMutation<any, any>(CANCEL_ORDER, { variables: { input: { id: order.id } } });
    const doCancel = () => {
      setShowConfirm(false);
      cancel();
    };

    return (
      <>
        <IconButton disabled={called || loading} onClick={() => setShowConfirm(true)} size="small" title="Avbryt ordre">
          <Cancel />
        </IconButton>
        <ConfirmDialog
          open={showConfirm}
          title="Avbryt ordre"
          message={`Vil du avbryte ordre ${order.orderNumber}?`}
          confirmText="Avbryt ordre"
          denyText="Lukk"
          deny={() => setShowConfirm(false)}
          confirm={doCancel}
        />
      </>
    );
  }




  function RetryPurchaseAction({ order }: { order: Order }) {
    const [showConfirm, setShowConfirm] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [retryErrorMessage, setRetryErrorMessage] = useState('');
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [retrySuccessMessage, setRetrySuccessMessage] = useState('');
    const [retryPurchase, { called, loading }] = useMutation<any, any>(RETRY_PURCHASE, {
      variables: { input: { orderNumber: order.orderNumber } },
      refetchQueries: [GET_ORDERS],
      onCompleted: () => {
        setRetrySuccessMessage(`Kjøpsprosessen ble startet på nytt for ordre ${order.orderNumber}.`);
        setShowSuccessDialog(true);
        console.log('Purchase retry initiated for order:', order.orderNumber);
      },
      onError: (error) => {
        const message = getRetryPurchaseErrorMessage(error, order.orderNumber);
        setRetryErrorMessage(message);
        setShowErrorDialog(true);
        console.error('Failed to retry purchase:', error);
      },
    });

    const doRetry = () => {
      setShowConfirm(false);
      retryPurchase();
    };

    return (
      <>
        <IconButton
          disabled={called || loading}
          onClick={() => setShowConfirm(true)}
          size="small"
          title="Prøv kjøp på nytt"
          color="primary"
        >
          <Replay />
        </IconButton>
        <ConfirmDialog
          open={showConfirm}
          title="Prøv kjøp på nytt"
          message={`Vil du prøve å kjøre kjøpsprosessen på nytt for ordre ${order.orderNumber}?${order.errorMessage ? `\n\nForrige feil: ${order.errorMessage}` : ''}`}
          confirmText="Prøv på nytt"
          denyText="Avbryt"
          deny={() => setShowConfirm(false)}
          confirm={doRetry}
        />
        <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)}>
          <DialogTitle>Kunne ikke prøve kjøpet på nytt</DialogTitle>
          <DialogContent>
            <Typography>{retryErrorMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={() => setShowErrorDialog(false)}>
              Lukk
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={showSuccessDialog} onClose={() => setShowSuccessDialog(false)}>
          <DialogTitle>Kjøp startet på nytt</DialogTitle>
          <DialogContent>
            <Typography>{retrySuccessMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={() => setShowSuccessDialog(false)}>
              Lukk
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  function UpdateStatusAction({ order }: { order: Order }) {
    const [showConfirm, setShowConfirm] = useState(false);

    return (
      <>
        <IconButton onClick={() => setShowConfirm(true)} size="small" title="Oppdater status">
          <GetApp />
        </IconButton>
        <ConfirmWithInvoiceDialog
          order={order}
          open={showConfirm}
          title="Oppdater status"
          orderNumber={order.orderNumber}
          deny={() => setShowConfirm(false)}
        />
      </>
    );
  }



  function CreditAction({ order }: { order: Order }) {
    const [showConfirm, setShowConfirm] = useState(false);
    const [credit, { called, loading }] = useMutation<any, any>(CREDIT_ORDER, { variables: { input: { id: order.id } } });
    const doCredit = () => {
      setShowConfirm(false);
      credit();
    };

    return (
      <>
        <IconButton disabled={called || loading} onClick={() => setShowConfirm(true)} size="small" title="Kreditér ordre">
          <Undo />
        </IconButton>
        <ConfirmDialog
          open={showConfirm}
          title="Kreditér ordre"
          message={`Vil du kreditére ordre ${order.orderNumber}?`}
          confirmText="Kreditér"
          deny={() => setShowConfirm(false)}
          confirm={doCredit}
        />
      </>
    );
  }

  return (
    <div>
      <FormControlLabel control={<Switch onChange={() => setShowCard(!showCard)} checked={showCard} />} label="Kort" />
      <FormControlLabel control={<Switch onChange={() => setShowInvoice(!showInvoice)} checked={showInvoice} />} label="Faktura" />
      <FormControlLabel
        control={<Switch onChange={() => setShowNonInvoiceable(!showNonInvoiceable)} checked={showNonInvoiceable} />}
        label="Ikke-fakturerbar"
      />
      <FormControlLabel control={<Switch onChange={() => setShowIncomplete(!showIncomplete)} checked={showIncomplete} />} label="Ufullstendige" />

      <div style={{ marginTop: 8, marginBottom: 8 }}>
        <SearchBox
          onSearch={handleSearch}
          onClearSearch={clearSearch}
          loading={loading || clientSearchLoading}
          hasActiveSearch={!!clientFilterValue || !!backendSearchTerm}
          searchInfo={searchInfo}
          resultCount={!isBackendSearch && clientFilterValue ? displayRows.length : undefined}
          totalCount={!isBackendSearch && clientFilterValue ? baseRows.length : undefined}
          currentSearchTerm={isBackendSearch ? backendSearchTerm : clientFilterValue}
        />
      </div>

      {isLoading && <Loading />}

      {!isLoading && (
        <DataGridPro
          apiRef={apiRef}
          rows={displayRows}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={rowCountState}
          pageSizeOptions={[100, 200, 500]}
          onPaginationModelChange={setPaginationModel}
          localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: paginationModel.pageSize,
                page: paginationModel.page,
              },
            },
          }}
          filterMode="client"
          disableColumnFilter
        />
      )}
    </div>
  );
}
